import { UploadManager } from "@bytescale/sdk"
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query"
import { atom, useAtom } from "jotai"
import Cookies from "js-cookie"
import pluralize from "pluralize"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import useUser from "@hooks/useUser"

import replaceSlugs from "@utils/replaceSlugs"

import { CommitResult, FileUploadFile } from "@molecules/FileUpload/FileUpload.types"
import toast from "@molecules/Toast/Toast"

import { FILE_UPLOAD_ENDPOINTS } from "@endpoints/fileUpload"

import { FILE_UPLOAD_VERSION } from "@constants/fileUpload"
import { TIME } from "@constants/time"

interface UploadConfigResponse {
    account_id: string
    base_path: string
}

interface AttachmentResponse {
    id: string
}

interface CreateAttachmentPayload {
    endpoint: string
    file: FileUploadFile
    filePath: string
}

interface DeleteAttachmentPayload {
    endpoint: string
}

const getAuthHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
})

const fetchUploadConfig = async (endpoint: string): Promise<UploadConfigResponse> => {
    const response = await fetch(endpoint, {
        method: "GET",
        headers: getAuthHeaders(),
        credentials: "include",
    })

    if (!response.ok) {
        const body = await response.text()
        console.error("Failed to fetch upload config:", { status: response.status, body })
        throw new Error(`Failed to fetch upload config: ${response.statusText}`)
    }

    return (await response.json()) as UploadConfigResponse
}

const createAttachment: MutationFunction<AttachmentResponse, CreateAttachmentPayload> = async (payload) => {
    const { endpoint, file, filePath } = payload

    const response = await fetch(endpoint, {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify({
            upload_filename: file.name,
            upload_mimetype: file.file.type,
            upload_size: file.file.size,
            file_path: filePath,
            is_public: true,
            version: FILE_UPLOAD_VERSION.BYTESCALE,
        }),
    })

    if (!response.ok) {
        const body = await response.text()
        console.error("Failed to create attachment:", { status: response.status, body })
        throw new Error(`Failed to create attachment: ${response.statusText}`)
    }

    return (await response.json()) as AttachmentResponse
}

const deleteAttachment: MutationFunction<void, DeleteAttachmentPayload> = async (payload) => {
    const { endpoint } = payload

    const response = await fetch(endpoint, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: "include",
    })

    if (!response.ok) {
        throw new Error(`Failed to delete attachment: ${response.statusText}`)
    }
}

interface StagedAddition {
    file: FileUploadFile
    filePath: string
    fileUrl: string
}

interface StagedDeletion {
    attachmentId: string
    deleteEndpoint: string
}

interface PersistedUploadState {
    stagedAdditions: Map<string, StagedAddition>
    stagedDeletions: Map<string, StagedDeletion>
    pendingFilePaths: string[]
    internalFiles: FileUploadFile[]
    deletedAttachmentIds: Set<string>
}

const emptyPersistedState = (): PersistedUploadState => ({
    stagedAdditions: new Map(),
    stagedDeletions: new Map(),
    pendingFilePaths: [],
    internalFiles: [],
    deletedAttachmentIds: new Set(),
})

/**
 * Global atom that persists staged upload state across component unmount/remount cycles.
 * Keyed by a storage key derived from the mode, objectType, and objectId/sessionId.
 * This allows the FileUpload component to survive sub-form navigation in legacy forms.
 */
const fileUploadStateAtom = atom<Map<string, PersistedUploadState>>(new Map())

interface UseFileUploadOptions {
    objectType?: ObjectType
    objectId?: string
    isPendingMode?: boolean
    sessionId?: string
    autoCommit?: boolean
    onFileUploadComplete?: (file: FileUploadFile, filePath: string, fileUrl: string) => void
    onPendingFileUploadComplete?: (file: FileUploadFile, filePath: string, fileUrl: string, sessionId: string) => void
    onFileUploadError?: (file: FileUploadFile, error: Error) => void
    onCommitComplete?: (result: CommitResult) => void
    onDiscardComplete?: () => void
}

interface UploadHandle {
    cancellationToken: { isCancelled: boolean }
}

type SingleFileUploadResult =
    | { status: "success"; filePath: string; attachmentId?: string }
    | { status: "cancelled" }
    | { status: "error" }

interface UseFileUploadReturn {
    uploadFiles: (files: FileUploadFile[]) => Promise<void>
    cancelUpload: (fileId: string) => void
    deleteFile: (fileId: string) => Promise<boolean>
    commitChanges: () => Promise<CommitResult>
    discardChanges: () => Promise<void>
    deleteExistingAttachment: (attachmentId: string) => Promise<boolean>
    persistComponentState: (internalFiles: FileUploadFile[], deletedAttachmentIds: Set<string>) => void
    persistedInternalFiles: FileUploadFile[]
    persistedDeletedAttachmentIds: Set<string>
    isUploading: boolean
    uploadProgress: Map<string, { progress: number; status: FileUploadFile["status"] }>
    attachmentIds: Map<string, string>
}

const MAX_RETRIES = 3
const RETRY_BASE_DELAY_MS = 1000

export default function useFileUpload(options: UseFileUploadOptions): UseFileUploadReturn {
    const {
        objectType,
        objectId,
        isPendingMode,
        sessionId: providedSessionId,
        autoCommit = true,
        onFileUploadComplete,
        onPendingFileUploadComplete,
        onFileUploadError,
        onCommitComplete,
        onDiscardComplete,
    } = options
    const { user } = useUser()

    // Generate session ID if in pending mode and not provided
    const [generatedSessionId] = useState(() => (isPendingMode && !providedSessionId ? crypto.randomUUID() : ""))
    const sessionId = providedSessionId || generatedSessionId

    // Persisted state: survives component unmount/remount (e.g., sub-form navigation).
    // Key uses objectId (attached mode) or sessionId (pending mode) for uniqueness.
    // Pending mode requires a stable sessionId from the container to avoid cross-session leaks.
    const storageKey = isPendingMode
        ? `pending:${objectType ?? ""}:${sessionId}`
        : `attached:${objectType ?? ""}:${objectId ?? ""}`
    const [globalUploadState, setGlobalUploadState] = useAtom(fileUploadStateAtom)
    const persisted = globalUploadState.get(storageKey) ?? emptyPersistedState()

    const [isUploading, setIsUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState<
        Map<string, { progress: number; status: FileUploadFile["status"] }>
    >(new Map())
    const [attachmentIds, setAttachmentIds] = useState<Map<string, string>>(new Map())

    const uploadHandlesRef = useRef<Map<string, UploadHandle>>(new Map())
    const uploadManagerRef = useRef<UploadManager | null>(null)
    const pendingFilePathsRef = useRef<string[]>(persisted.pendingFilePaths)
    const stagedAdditionsRef = useRef<Map<string, StagedAddition>>(persisted.stagedAdditions)
    const stagedDeletionsRef = useRef<Map<string, StagedDeletion>>(persisted.stagedDeletions)
    const suppressToastRef = useRef(false)
    const autoCommitRef = useRef(autoCommit)
    autoCommitRef.current = autoCommit
    const isPendingModeRef = useRef(isPendingMode)
    isPendingModeRef.current = isPendingMode

    const serviceCompanySlug: string = (user?.service_company?.slug as string) ?? ""

    const uploadConfigEndpoint = useMemo(() => {
        if (isPendingMode) {
            // Use pending upload endpoint - doesn't require object ID
            const baseUrl = FILE_UPLOAD_ENDPOINTS.PENDING_UPLOAD_CONFIG
            return sessionId ? `${baseUrl}?session_id=${sessionId}` : baseUrl
        }
        return replaceSlugs(FILE_UPLOAD_ENDPOINTS.UPLOAD_CONFIG, {
            service_company_slug: serviceCompanySlug,
            // Django routes use hyphens (e.g. "purchase-orders"), not underscores
            object_type: objectType ? `${objectType}s`.replaceAll("_", "-") : "",
            object_id: objectId ?? "",
        })
    }, [isPendingMode, sessionId, serviceCompanySlug, objectType, objectId])

    const attachmentsEndpoint = useMemo(
        () =>
            replaceSlugs(FILE_UPLOAD_ENDPOINTS.ATTACHMENTS, {
                service_company_slug: serviceCompanySlug,
                // Django routes use hyphens (e.g. "purchase-orders"), not underscores
                object_type: objectType ? `${objectType}s`.replaceAll("_", "-") : "",
                object_id: objectId ?? "",
            }),
        [serviceCompanySlug, objectType, objectId],
    )

    const { data: configData, refetch: refetchConfig } = useQuery({
        queryKey: ["uploadConfig", uploadConfigEndpoint],
        queryFn: () => fetchUploadConfig(uploadConfigEndpoint),
        enabled: false,
        staleTime: TIME.FIVE_MINUTES,
    })

    const createAttachmentMutation = useMutation({
        mutationFn: createAttachment,
        onError: (error) => {
            console.error("Failed to create attachment record:", error)
        },
    })

    const deleteAttachmentMutation = useMutation({
        mutationFn: deleteAttachment,
        onSuccess: () => {
            if (!suppressToastRef.current) {
                toast({
                    type: "success",
                    title: "File deleted",
                })
            }
        },
        onError: (error) => {
            console.error("Failed to delete attachment:", error)
            if (!suppressToastRef.current) {
                toast({
                    type: "error",
                    variant: "detailed",
                    title: "Could not delete file",
                    supportText: "Something went wrong. Please try again.",
                })
            }
        },
    })

    const getUploadManager = useCallback(async (): Promise<{ manager: UploadManager; basePath: string }> => {
        let config = configData
        if (!config) {
            const { data } = await refetchConfig()
            config = data
        }
        if (!config) {
            throw new Error("Failed to fetch upload configuration")
        }

        if (!uploadManagerRef.current) {
            const apiKey = process.env.BYTESCALE_PUBLIC_KEY
            if (!apiKey) {
                throw new Error("Missing BYTESCALE_PUBLIC_KEY environment variable")
            }
            uploadManagerRef.current = new UploadManager({ apiKey })
        }

        return { manager: uploadManagerRef.current, basePath: config.base_path }
    }, [configData, refetchConfig])

    const updateProgress = useCallback((fileId: string, progress: number, status: FileUploadFile["status"]) => {
        setUploadProgress((prev) => {
            const next = new Map(prev)
            next.set(fileId, { progress, status })
            return next
        })
    }, [])

    const removeProgress = useCallback((fileId: string) => {
        setUploadProgress((prev) => {
            const next = new Map(prev)
            next.delete(fileId)
            return next
        })
    }, [])

    const cleanupOrphanedFile = useCallback(async (filePath: string) => {
        try {
            await fetch(FILE_UPLOAD_ENDPOINTS.REMOVE_ORPHANED_FILE, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
                body: JSON.stringify({ file_path: filePath }),
            })
        } catch (error) {
            console.warn("Failed to clean up orphaned file:", error)
        }
    }, [])

    const createAttachmentRecord = useCallback(
        async (file: FileUploadFile, filePath: string): Promise<string> => {
            const result = await createAttachmentMutation.mutateAsync({
                endpoint: attachmentsEndpoint,
                file,
                filePath,
            })
            return result.id
        },
        [createAttachmentMutation, attachmentsEndpoint],
    )

    const uploadSingleFile = useCallback(
        async (file: FileUploadFile, manager: UploadManager, basePath: string): Promise<SingleFileUploadResult> => {
            let retryCount = 0
            const cancellationToken = { isCancelled: false }

            uploadHandlesRef.current.set(file.id, { cancellationToken })

            const attemptUpload = async (): Promise<SingleFileUploadResult> => {
                try {
                    updateProgress(file.id, 0, "uploading")

                    const timestamp = Date.now()
                    const uniqueFileName = `${crypto.randomUUID().slice(-12)}_${timestamp}_${file.name}`

                    const { fileUrl, filePath: uploadedPath } = await manager.upload({
                        data: file.file,
                        path: {
                            folderPath: basePath,
                            fileName: uniqueFileName,
                        },
                        onProgress: ({ progress }) => {
                            updateProgress(file.id, Math.round(progress), "uploading")
                        },
                        cancellationToken,
                    })

                    // Check cancellation after upload completes but before record creation/staging
                    // This fixes the race where upload wins against user cancellation
                    if (cancellationToken.isCancelled) {
                        await cleanupOrphanedFile(uploadedPath)
                        return { status: "cancelled" }
                    }

                    // File is now in ByteScale — notify per-file callbacks
                    if (isPendingMode) {
                        pendingFilePathsRef.current.push(uploadedPath)
                        onPendingFileUploadComplete?.(file, uploadedPath, fileUrl, sessionId)
                    } else {
                        onFileUploadComplete?.(file, uploadedPath, fileUrl)
                    }

                    // Handle attachment record creation/staging (attached mode only)
                    let createdAttachmentId: string | undefined
                    if (!isPendingMode) {
                        if (autoCommit) {
                            // Immediate mode: create attachment record now
                            try {
                                createdAttachmentId = await createAttachmentRecord(file, uploadedPath)
                                setAttachmentIds((prev) => {
                                    const next = new Map(prev)
                                    next.set(file.id, createdAttachmentId!)
                                    return next
                                })
                            } catch (error) {
                                console.error("Failed to create attachment, cleaning up orphaned file:", error)
                                await cleanupOrphanedFile(uploadedPath)
                                throw error
                            }
                        } else {
                            // Deferred mode: stage the addition for later commit
                            stagedAdditionsRef.current.set(file.id, { file, filePath: uploadedPath, fileUrl })
                        }
                    }

                    updateProgress(file.id, 100, "complete")
                    uploadHandlesRef.current.delete(file.id)
                    return { status: "success", filePath: uploadedPath, attachmentId: createdAttachmentId }
                } catch (error) {
                    if (cancellationToken.isCancelled) {
                        return { status: "cancelled" }
                    }

                    console.error("File upload failed:", file.name, error)

                    const errorMessage = error instanceof Error ? error.message.toLowerCase() : ""
                    const isRetryableError =
                        errorMessage.includes("network") ||
                        errorMessage.includes("fetch") ||
                        errorMessage.includes("timeout") ||
                        errorMessage.includes("timed out") ||
                        errorMessage.includes("failed to upload part") ||
                        (error instanceof Error && error.name === "TypeError")

                    if (retryCount < MAX_RETRIES && isRetryableError) {
                        retryCount++
                        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retryCount - 1)
                        await new Promise((resolve) => setTimeout(resolve, delay))
                        return attemptUpload()
                    }

                    updateProgress(file.id, 0, "error")
                    uploadHandlesRef.current.delete(file.id)
                    onFileUploadError?.(file, error instanceof Error ? error : new Error("Upload failed"))
                    return { status: "error" }
                }
            }

            return attemptUpload()
        },
        [
            autoCommit,
            createAttachmentRecord,
            cleanupOrphanedFile,
            isPendingMode,
            sessionId,
            onFileUploadComplete,
            onPendingFileUploadComplete,
            onFileUploadError,
            updateProgress,
        ],
    )

    const uploadFiles = useCallback(
        async (files: FileUploadFile[]): Promise<void> => {
            if (files.length === 0) {
                return
            }

            setIsUploading(true)

            const fileCount = files.length
            const fileWord = pluralize("file", fileCount)

            try {
                const { manager, basePath } = await getUploadManager()
                const results = await Promise.all(files.map((file) => uploadSingleFile(file, manager, basePath)))

                const cancelledCount = results.filter((result) => result.status === "cancelled").length
                const successCount = results.filter((result) => result.status === "success").length
                const effectiveTotal = fileCount - cancelledCount

                if (cancelledCount > 0 && effectiveTotal === 0) {
                    // All files were cancelled
                    toast({
                        type: "info",
                        title: "Upload canceled",
                    })
                } else if (effectiveTotal > 0) {
                    if (successCount === effectiveTotal) {
                        toast({
                            type: "success",
                            title: `${pluralize("File", successCount)} uploaded`,
                        })
                    } else if (successCount > 0) {
                        toast({
                            type: "error",
                            variant: "detailed",
                            title: `Could not upload all ${fileWord}`,
                            supportText: `${successCount} of ${effectiveTotal} uploaded successfully.`,
                        })
                    } else {
                        toast({
                            type: "error",
                            variant: "detailed",
                            title: `Could not upload ${pluralize("file", effectiveTotal)}`,
                            supportText: "Something went wrong. Please try again.",
                        })
                    }

                    // Show additional cancel toast if some (but not all) were cancelled
                    if (cancelledCount > 0) {
                        toast({
                            type: "info",
                            title: `${cancelledCount} ${pluralize("upload", cancelledCount)} canceled`,
                        })
                    }
                }

                // In autoCommit mode, records were created inline — build and report the batch result
                if (autoCommit && !isPendingMode && successCount > 0) {
                    const commitResult: CommitResult = {
                        success: true,
                        createdAttachments: files.reduce<CommitResult["createdAttachments"]>(
                            (created, file, index) => {
                                const result = results[index]
                                if (result.status === "success") {
                                    created.push({
                                        fileId: file.id,
                                        attachmentId: result.attachmentId ?? "",
                                        fileName: file.name,
                                        mimeType: file.type,
                                        fileSize: file.size,
                                        filePath: result.filePath,
                                    })
                                }
                                return created
                            },
                            [],
                        ),
                        deletedAttachmentIds: [],
                        errors: [],
                    }
                    onCommitComplete?.(commitResult)
                }
            } catch (error) {
                console.error("Upload setup failed:", error)
                toast({
                    type: "error",
                    variant: "detailed",
                    title: `Could not upload ${fileWord}`,
                    supportText: "Something went wrong. Please try again.",
                })
            } finally {
                setIsUploading(false)
            }
        },
        [getUploadManager, uploadSingleFile, autoCommit, isPendingMode, onCommitComplete],
    )

    const cancelUpload = useCallback(
        (fileId: string) => {
            const handle = uploadHandlesRef.current.get(fileId)
            if (handle) {
                handle.cancellationToken.isCancelled = true
                uploadHandlesRef.current.delete(fileId)
                removeProgress(fileId)
            }
        },
        [removeProgress],
    )

    const deleteFile = useCallback(
        async (fileId: string): Promise<boolean> => {
            // In deferred mode, check if this file is a staged addition (not yet committed)
            if (!autoCommit && stagedAdditionsRef.current.has(fileId)) {
                const staged = stagedAdditionsRef.current.get(fileId)!
                stagedAdditionsRef.current.delete(fileId)
                await cleanupOrphanedFile(staged.filePath)
                removeProgress(fileId)
                return true
            }

            const attachmentId = attachmentIds.get(fileId)
            if (!attachmentId) {
                return false
            }

            const deleteEndpoint = replaceSlugs(FILE_UPLOAD_ENDPOINTS.ATTACHMENT, {
                service_company_slug: serviceCompanySlug,
                object_type: objectType ? `${objectType}s`.replaceAll("_", "-") : "",
                object_id: objectId ?? "",
                attachment_id: attachmentId,
            })

            if (!autoCommit) {
                // Deferred mode: stage the deletion instead of firing DELETE
                stagedDeletionsRef.current.set(attachmentId, { attachmentId, deleteEndpoint })
            } else {
                // Immediate mode: fire DELETE now
                try {
                    await deleteAttachmentMutation.mutateAsync({ endpoint: deleteEndpoint })
                } catch {
                    return false
                }
            }

            setAttachmentIds((prev) => {
                const next = new Map(prev)
                next.delete(fileId)
                return next
            })
            removeProgress(fileId)
            return true
        },
        [
            autoCommit,
            attachmentIds,
            serviceCompanySlug,
            objectType,
            objectId,
            deleteAttachmentMutation,
            removeProgress,
            cleanupOrphanedFile,
        ],
    )

    /**
     * Delete a pre-existing server-side attachment (one that was in `existingAttachments`).
     *
     * - `autoCommit=false`: stages the deletion for the next `commitChanges()` call.
     * - `autoCommit=true`: fires the DELETE immediately and calls `onCommitComplete`
     *   with `createdAttachments: []` and `deletedAttachmentIds: [attachmentId]`.
     */
    const deleteExistingAttachment = useCallback(
        async (attachmentId: string): Promise<boolean> => {
            const deleteEndpoint = replaceSlugs(FILE_UPLOAD_ENDPOINTS.ATTACHMENT, {
                service_company_slug: serviceCompanySlug,
                object_type: objectType ? `${objectType}s`.replaceAll("_", "-") : "",
                object_id: objectId ?? "",
                attachment_id: attachmentId,
            })

            if (!autoCommit) {
                // Deferred mode: stage the deletion for later commit
                stagedDeletionsRef.current.set(attachmentId, { attachmentId, deleteEndpoint })
                return true
            }

            // Immediate mode: fire DELETE now
            try {
                await deleteAttachmentMutation.mutateAsync({ endpoint: deleteEndpoint })
                onCommitComplete?.({
                    success: true,
                    createdAttachments: [],
                    deletedAttachmentIds: [attachmentId],
                    errors: [],
                })
                return true
            } catch {
                return false
            }
        },
        [serviceCompanySlug, objectType, objectId, autoCommit, deleteAttachmentMutation, onCommitComplete],
    )

    const persistComponentState = useCallback(
        (internalFiles: FileUploadFile[], deletedAttachmentIds: Set<string>) => {
            setGlobalUploadState((prev) => {
                const next = new Map(prev)
                next.set(storageKey, {
                    stagedAdditions: new Map(stagedAdditionsRef.current),
                    stagedDeletions: new Map(stagedDeletionsRef.current),
                    pendingFilePaths: [...pendingFilePathsRef.current],
                    internalFiles,
                    deletedAttachmentIds,
                })
                return next
            })
        },
        [storageKey, setGlobalUploadState],
    )

    const clearPersistedState = useCallback(() => {
        setGlobalUploadState((prev) => {
            const next = new Map(prev)
            next.delete(storageKey)
            return next
        })
    }, [storageKey, setGlobalUploadState])

    const commitChanges = useCallback(async (): Promise<CommitResult> => {
        const result: CommitResult = { success: true, createdAttachments: [], deletedAttachmentIds: [], errors: [] }

        suppressToastRef.current = true

        try {
            // Fire all staged additions (create attachment records)
            for (const [fileId, addition] of stagedAdditionsRef.current) {
                try {
                    const attachmentId = await createAttachmentRecord(addition.file, addition.filePath)
                    result.createdAttachments.push({
                        fileId,
                        attachmentId,
                        fileName: addition.file.name,
                        mimeType: addition.file.type,
                        fileSize: addition.file.size,
                        filePath: addition.filePath,
                    })
                    setAttachmentIds((prev) => {
                        const next = new Map(prev)
                        next.set(fileId, attachmentId)
                        return next
                    })
                } catch (error) {
                    result.success = false
                    result.errors.push(error instanceof Error ? error : new Error("Failed to create attachment"))
                    await cleanupOrphanedFile(addition.filePath)
                }
            }

            // Fire all staged deletions
            for (const [attachmentId, deletion] of stagedDeletionsRef.current) {
                try {
                    await deleteAttachmentMutation.mutateAsync({ endpoint: deletion.deleteEndpoint })
                    result.deletedAttachmentIds.push(attachmentId)
                } catch (error) {
                    result.success = false
                    result.errors.push(error instanceof Error ? error : new Error("Failed to delete attachment"))
                }
            }

            // Clear staged state
            stagedAdditionsRef.current.clear()
            stagedDeletionsRef.current.clear()
        } finally {
            suppressToastRef.current = false
        }

        clearPersistedState()
        onCommitComplete?.(result)
        return result
    }, [createAttachmentRecord, cleanupOrphanedFile, deleteAttachmentMutation, onCommitComplete, clearPersistedState])

    const discardChanges = useCallback(async () => {
        // Deferred mode: clean up staged additions
        const stagedCleanup = Array.from(stagedAdditionsRef.current.values()).map((addition) =>
            cleanupOrphanedFile(addition.filePath),
        )

        // Pending mode: clean up pending uploads
        const pendingCleanup = pendingFilePathsRef.current.map((filePath) => cleanupOrphanedFile(filePath))

        await Promise.all([...stagedCleanup, ...pendingCleanup])

        stagedAdditionsRef.current.clear()
        stagedDeletionsRef.current.clear()
        pendingFilePathsRef.current = []

        clearPersistedState()
        onDiscardComplete?.()
    }, [cleanupOrphanedFile, onDiscardComplete, clearPersistedState])

    // NOTE: We intentionally do NOT clean up on unmount. The FileUpload component
    // unmounts/remounts during normal form interactions (sub-forms, previews, line item
    // editing), so unmount does not mean cancellation. Instead, cleanup is handled by:
    // - Explicit discard via triggerDiscard (Cancel button in consumer forms)
    // - beforeunload listener below (tab close / page refresh)

    // beforeunload listener: clean up orphaned files if the user closes/refreshes the page
    useEffect(() => {
        if (autoCommit && !isPendingMode) {
            return
        }

        const handleBeforeUnload = () => {
            // Deferred mode: clean up staged additions
            if (!autoCommit) {
                for (const addition of stagedAdditionsRef.current.values()) {
                    void fetch(FILE_UPLOAD_ENDPOINTS.REMOVE_ORPHANED_FILE, {
                        method: "POST",
                        headers: getAuthHeaders(),
                        credentials: "include",
                        body: JSON.stringify({ file_path: addition.filePath }),
                        keepalive: true,
                    })
                }
            }

            // Pending mode: clean up pending uploads
            if (isPendingMode) {
                for (const filePath of pendingFilePathsRef.current) {
                    void fetch(FILE_UPLOAD_ENDPOINTS.REMOVE_ORPHANED_FILE, {
                        method: "POST",
                        headers: getAuthHeaders(),
                        credentials: "include",
                        body: JSON.stringify({ file_path: filePath }),
                        keepalive: true,
                    })
                }
            }

            // Clear persisted Jotai state — page is closing, no need to keep it
            clearPersistedState()
        }

        window.addEventListener("beforeunload", handleBeforeUnload)
        return () => window.removeEventListener("beforeunload", handleBeforeUnload)
    }, [autoCommit, isPendingMode, clearPersistedState])

    return {
        uploadFiles,
        cancelUpload,
        deleteFile,
        commitChanges,
        discardChanges,
        deleteExistingAttachment,
        persistComponentState,
        persistedInternalFiles: persisted.internalFiles,
        persistedDeletedAttachmentIds: persisted.deletedAttachmentIds,
        isUploading,
        uploadProgress,
        attachmentIds,
    }
}
